import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import isValidURL from "../../ValidateUrl";
import { updateUser } from "../../store/slice/user";
import {
  handleShowProfile,
  handleUpdateProfile,
  handleUpdateAddress,
} from "../auth/Auth";
import Header from "../components/Header";
import { useNavigate } from "react-router-dom";
import { useGoogleMapsLoader } from "../../GoogleMapsProvider";
import MapWithConditionalRendering from "../components/Map/MapWithConditionRendering";

export const UserProfile = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  const [isPending, setIsPending]   = useState(false);
  const [shouldReload, setShouldReload] = useState(false);

  const [profileData, setProfileData] = useState({
    id: "", first_name: "", last_name: "",
    email: "", phone: "", profile_pic: "",
  });

  const [imageToUpload, setImageToUpload] = useState("");

  // address — set only via reverseGeocode callback or initial profile load
  const [address, setAddress] = useState("");

  // coordinates — set via placeSelect, drag, GPS, or initial profile load
  const [coordinates, setCoordinates] = useState({ lat: "", lng: "" });

  // True only after user actively interacts with map/landmark/GPS
  const [isLocationConfirmed, setIsLocationConfirmed] = useState(false);

  const { authToken } = useSelector((state) => state.user);

  const { isLoaded } = useGoogleMapsLoader();

  const hasCoordinates = coordinates.lat !== "" && coordinates.lng !== "";

  // ── Fetch Profile ─────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const response = await handleShowProfile(authToken);
      if (!response) return;

      // Redirect chefs/admins away from user profile
      if (response.is_chef === 1 || response.is_admin === 1) {
        navigate("/shef/profile", { replace: true });
        return;
      }

      localStorage.setItem("user", JSON.stringify(response));
      dispatch(updateUser(response));
      setProfileData(response);

      if (response.user_addresses?.length > 0) {
        const last = response.user_addresses[response.user_addresses.length - 1];
        const lat  = parseFloat(last.latitude);
        const lng  = parseFloat(last.longitude);

        setAddress(last.address);
        setCoordinates({ lat, lng });

        // Pre-existing saved address = already confirmed
        if (lat && lng && last.address) setIsLocationConfirmed(true);
      }
    })();
  }, [authToken, dispatch, shouldReload]);

  // ── Callbacks from MapWithConditionalRendering ────────────────────────────

  // Called when autocomplete picks a place or GPS resolves — update coordinates
  const handlePlaceSelect = ({ lat, lng }) => {
    setCoordinates({ lat, lng });
  };

  // Called whenever a reverse geocode completes (drag, GPS, landmark autocomplete)
  // This is the ONLY place that sets the address string
  const handleReverseGeocode = (formattedAddress) => {
    setAddress(formattedAddress);
  };

  // Called when marker is dragged — map component calls onReverseGeocode too,
  // so we only need to sync coordinates here
  const onDragEnd = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setCoordinates({ lat, lng });
  };

  // ── Form field change ─────────────────────────────────────────────────────
  const handleProfileDataChange = (e) => {
    setProfileData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleOnSubmit = async (e) => {
    e.preventDefault();

    if (!isLocationConfirmed || !hasCoordinates) {
      toast.error("Please confirm your location using the map or landmark search.");
      return;
    }

    if (!address.trim()) {
      toast.error("Address could not be resolved. Please try again.");
      return;
    }

    setIsPending(true);
    try {
      const formData = new FormData();
      formData.append("id",         profileData.id);
      formData.append("first_name", profileData.first_name);
      formData.append("last_name",  profileData.last_name);
      formData.append("email",      profileData.email);
      formData.append("phone",      profileData.phone);
      if (imageToUpload) formData.append("profile_pic", imageToUpload);

      const addressFormData = new FormData();
      addressFormData.append("name",      profileData.first_name);
      addressFormData.append("phone",     profileData.phone);
      addressFormData.append("address",   address);
      addressFormData.append("latitude",  coordinates.lat);
      addressFormData.append("longitude", coordinates.lng);

      await handleUpdateProfile(authToken, formData);
      const addressResponse = await handleUpdateAddress(authToken, addressFormData);

      // Sync back from server response if available
      if (addressResponse?.address)   setAddress(addressResponse.address);
      if (addressResponse?.latitude && addressResponse?.longitude) {
        setCoordinates({
          lat: parseFloat(addressResponse.latitude),
          lng: parseFloat(addressResponse.longitude),
        });
      }

      toast.success("Profile & Address Updated");
      setShouldReload((prev) => !prev);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsPending(false);
    }
  };

  // ── Image ─────────────────────────────────────────────────────────────────
  const fileInputRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageToUpload(file);
    const reader = new FileReader();
    reader.onload = () => setProfileData((prev) => ({ ...prev, profile_pic: reader.result }));
    reader.readAsDataURL(file);
  };

  if (!isLoaded) return null;

  const isSubmitDisabled = isPending || !isLocationConfirmed || !hasCoordinates;

  return (
    <>
      <Header />
      <div className="container mx-auto p-5">
        <div className="p-5 rounded-xl border border-borderClr mb-6">
          <h2 className="text-xl font-semibold border-b pb-2 mb-4">Profile Info</h2>

          <form onSubmit={handleOnSubmit}>

            {/* ── Profile Picture ── */}
            <div
              className="w-[170px] h-[190px] border border-borderClr rounded-lg overflow-hidden cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              {profileData.profile_pic && isValidURL(profileData.profile_pic) ? (
                <img src={profileData.profile_pic} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <p className="flex items-center justify-center h-full text-sm text-[#777]">
                  Click to add Profile
                </p>
              )}
            </div>
            <input type="file" accept="image/*" onChange={handleImageChange} ref={fileInputRef} hidden />

            <div className="grid grid-cols-12 gap-4 mt-6">

              {/* ── Name / Email / Phone ── */}
              <div className="col-span-6">
                <input name="first_name" value={profileData.first_name}
                  onChange={handleProfileDataChange} placeholder="First Name" />
              </div>
              <div className="col-span-6">
                <input name="last_name" value={profileData.last_name}
                  onChange={handleProfileDataChange} placeholder="Last Name" />
              </div>
              <div className="col-span-6">
                <input value={profileData.email} readOnly className="read-only:bg-gray-50" />
              </div>
              <div className="col-span-6">
                <input name="phone" value={profileData.phone}
                  onChange={handleProfileDataChange} placeholder="03XXXXXXXXX" />
              </div>

              {/* ── Address (read-only, populated by map) ── */}
              <div className="col-span-12">
                <label className="block text-sm font-semibold mb-1 uppercase">
                  Address <span className="text-primary">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={address}
                    readOnly
                    placeholder="Auto-filled when you select a location below"
                    className={`w-full border rounded-md px-3 py-2 pr-9 text-sm bg-gray-50 cursor-default select-none
                      ${isLocationConfirmed && address
                        ? "border-green-400 bg-green-50 text-green-800"
                        : "border-gray-300 text-gray-500"
                      }`}
                  />
                  {isLocationConfirmed && address && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                  )}
                </div>
                {!isLocationConfirmed && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    Use the landmark search or drag the map pin to confirm your location.
                  </p>
                )}
              </div>

              {/* ── Map + Landmark Search ── */}
              <div className="col-span-12">
                <label className="block text-sm font-semibold mb-2 uppercase">
                  Pin Your Location <span className="text-primary">*</span>
                </label>
                <div className="border rounded-lg overflow-hidden p-3 bg-white">
                  <MapWithConditionalRendering
                    coordinates={hasCoordinates ? coordinates : { lat: 24.8607, lng: 67.0011 }}
                    onDragEnd={onDragEnd}
                    showMap={true}
                    onPlaceSelect={handlePlaceSelect}
                    onReverseGeocode={handleReverseGeocode}
                    onLocationConfirmed={setIsLocationConfirmed}
                  />
                </div>
              </div>

              {/* ── Submit ── */}
              <div className="col-span-12 flex flex-col items-start gap-1">
                {isSubmitDisabled && !isPending && (
                  <p className="text-xs text-gray-400">
                    {!isLocationConfirmed
                      ? "📍 Confirm your location to enable submit"
                      : "Fix errors above to continue"}
                  </p>
                )}
                <button
                  disabled={isSubmitDisabled}
                  className="bg-primary text-white px-6 py-2 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                >
                  {isPending ? (
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg"
                        fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 100 8v4a8 8 0 01-8-8z"/>
                      </svg>
                      Saving...
                    </div>
                  ) : "Submit"}
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UserProfile;